import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Zap, Loader2, CreditCard, ShieldCheck, AlertTriangle } from "lucide-react";

export default function AutoRechargeModal({ open, onOpenChange }) {
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [threshold, setThreshold] = useState(3000);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingCard, setSavingCard] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auto-recharge");
      setData(data);
      setThreshold(data.threshold);
      setEnabled(data.enabled);
    } catch {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const saveCard = async () => {
    setSavingCard(true);
    try {
      const { data } = await api.post("/auto-recharge/setup", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
      setSavingCard(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/auto-recharge", { enabled, threshold: Number(threshold) });
      await refresh();
      await load();
      toast.success("Preferenze auto-ricarica salvate");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md border-border/60 bg-card" data-testid="auto-recharge-modal">
        <DialogHeader>
          <div className="mx-auto h-11 w-11 rounded-2xl bg-brand-blue/10 flex items-center justify-center mb-1">
            <Zap className="h-5 w-5 text-brand-blue" />
          </div>
          <DialogTitle className="text-center font-heading text-xl font-medium">Auto-ricarica</DialogTitle>
          <DialogDescription className="text-center text-sm text-muted-foreground">
            Ricarica automaticamente le battute quando il saldo scende, senza interruzioni.
          </DialogDescription>
        </DialogHeader>

        {loading || !data ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-brand-blue" /></div>
        ) : (
          <div className="space-y-5">
            {data.status && (
              <div className="rounded-xl border border-border bg-background/50 px-3 py-2 text-xs text-muted-foreground" data-testid="ar-status">
                {data.status}
              </div>
            )}

            {!data.has_card ? (
              <div className="rounded-xl border border-brand-blue/30 bg-brand-blue/5 p-4 text-center space-y-3" data-testid="ar-no-card">
                <CreditCard className="h-7 w-7 mx-auto text-brand-blue" />
                <p className="text-sm">Per attivare l'auto-ricarica salva una carta in modo sicuro tramite Stripe.</p>
                <Button data-testid="ar-save-card-btn" onClick={saveCard} disabled={savingCard}
                  className="rounded-full bg-brand-blue hover:bg-brand-blue/90 text-white gap-2">
                  {savingCard ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                  Salva carta
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between rounded-xl border border-border p-4">
                  <div>
                    <p className="text-sm font-medium">Auto-ricarica attiva</p>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                      <ShieldCheck className="h-3 w-3 text-emerald-500" /> Carta salvata
                    </p>
                  </div>
                  <Switch data-testid="ar-toggle" checked={enabled} onCheckedChange={setEnabled} />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground">Soglia (battute)</label>
                  <Input data-testid="ar-threshold" type="number" min={500} max={90000} step={500}
                    value={threshold} onChange={(e) => setThreshold(e.target.value)} className="rounded-full" />
                  <p className="text-xs text-muted-foreground">
                    Quando il saldo scende sotto questa soglia, addebitiamo <strong>{data.amount}</strong> per <strong>{data.chars.toLocaleString("it-IT")}</strong> battute.
                  </p>
                </div>

                <div className="flex items-start gap-2 text-[11px] text-muted-foreground">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-500" />
                  L'addebito avviene automaticamente sulla carta salvata. Puoi disattivare l'opzione in qualsiasi momento.
                </div>

                <Button data-testid="ar-save-btn" onClick={save} disabled={saving}
                  className="w-full rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Salva preferenze
                </Button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
